import { useState } from "react";
import { Music, Sparkles, Loader2, Flame } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { QUALITIES } from "./GeneratorForm";
import { YoutubePublishOptions } from "./YoutubePublishOptions";

const BHAJAN_PRESETS = [
  { key: "ganesh", label: "Ganesh ji Aarti", testid: "song-preset-ganesh" },
  { key: "krishna", label: "Shri Krishna Bhajan", testid: "song-preset-krishna" },
  { key: "shiv", label: "Shiv ji Tandav Stuti", testid: "song-preset-shiv" },
  { key: "hanuman", label: "Hanuman ji Chalisa Vibe", testid: "song-preset-hanuman" },
];

const SONG_PRESETS = [
  { key: "love", label: "A soulful love song", testid: "song-preset-love" },
  { key: "motivation", label: "Motivational anthem", testid: "song-preset-motivation" },
  { key: "monsoon", label: "Rainy monsoon melody", testid: "song-preset-monsoon" },
];

export const SongGeneratorForm = ({ onCreated }) => {
  const [songType, setSongType] = useState("bhajan");
  const [topic, setTopic] = useState("");
  const [language, setLanguage] = useState("hi");
  const [quality, setQuality] = useState("720p");
  const [submitting, setSubmitting] = useState(false);
  const [publishOpts, setPublishOpts] = useState({
    auto_publish: false,
    publish_privacy: "private",
    publish_at_landscape: "",
    publish_at_shorts: "",
  });

  const presets = songType === "bhajan" ? BHAJAN_PRESETS : SONG_PRESETS;

  const submit = async (e) => {
    e.preventDefault();
    if (!topic.trim()) {
      toast.error("Please enter a subject for the song");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/projects", {
        topic: topic.trim(),
        kind: "song",
        song_type: songType,
        language,
        quality,
        auto_publish: publishOpts.auto_publish,
        publish_privacy: publishOpts.publish_privacy,
        publish_at_landscape:
          publishOpts.publish_privacy === "schedule" && publishOpts.publish_at_landscape
            ? new Date(publishOpts.publish_at_landscape).toISOString()
            : null,
        publish_at_shorts:
          publishOpts.publish_privacy === "schedule" && publishOpts.publish_at_shorts
            ? new Date(publishOpts.publish_at_shorts).toISOString()
            : null,
      });
      onCreated(res.data.id);
    } catch (e) {
      toast.error("Failed to start song generation");
      setSubmitting(false);
    }
  };

  return (
    <div className="relative rounded-2xl p-[1px] bg-gradient-to-br from-fuchsia-500/40 via-slate-800 to-slate-800">
      <div
        className="rounded-2xl bg-slate-900/80 border border-slate-800/80 p-6 sm:p-8 backdrop-blur-md"
        style={{ backgroundImage: "radial-gradient(circle at 50% -20%, rgba(217, 70, 239, 0.12), transparent 70%)" }}
      >
        <div className="flex items-center gap-2 mb-1">
          <Music className="w-6 h-6 text-fuchsia-400" />
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-50">
            Song &amp; Bhajan Maker
          </h2>
        </div>
        <p className="text-sm text-slate-400 mb-6">
          AI writes the lyrics, Suno composes the music, and we turn each song into videos with
          the lyrics shown on screen. Every request creates <span className="text-fuchsia-300">4 videos</span> —
          2 landscape + 2 shorts (from 2 AI-composed songs). Rendered one at a time.
        </p>

        <div className="flex gap-2 mb-5">
          <button
            type="button"
            data-testid="song-type-bhajan"
            onClick={() => setSongType("bhajan")}
            className={`flex-1 flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm border transition-all ${
              songType === "bhajan" ? "bg-fuchsia-500 text-black border-fuchsia-500" : "bg-slate-950/80 border-slate-800 text-slate-300"
            }`}
          >
            <Flame className="w-4 h-4" /> Devotional Bhajan
          </button>
          <button
            type="button"
            data-testid="song-type-song"
            onClick={() => setSongType("song")}
            className={`flex-1 flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm border transition-all ${
              songType === "song" ? "bg-fuchsia-500 text-black border-fuchsia-500" : "bg-slate-950/80 border-slate-800 text-slate-300"
            }`}
          >
            <Music className="w-4 h-4" /> Regular Song
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-5">
          {presets.map((p) => (
            <button
              key={p.key}
              type="button"
              data-testid={p.testid}
              onClick={() => setTopic(p.label)}
              className="bg-slate-800/80 hover:bg-slate-700 text-slate-200 border border-slate-700/60 px-3 py-1.5 rounded-full text-xs transition-all"
            >
              {p.label}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-5">
          <input
            data-testid="song-topic-input"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder={songType === "bhajan" ? "e.g. Ganesh ji, Shri Krishna, Maa Durga..." : "e.g. chasing your dreams, first love, city nights..."}
            className="w-full bg-slate-950/80 border border-slate-800 focus:border-fuchsia-500 focus:ring-2 focus:ring-fuchsia-500/20 text-slate-100 placeholder-slate-500 rounded-xl p-4 text-base sm:text-lg transition-all outline-none"
          />

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2 block">Language</label>
              <select
                data-testid="song-language-select"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-full bg-slate-950/80 border border-slate-800 text-slate-200 rounded-xl px-4 py-2.5 focus:border-fuchsia-500 outline-none"
              >
                <option value="hi">Hindi</option>
                <option value="en">English</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2 block">Quality</label>
              <div className="flex gap-1.5">
                {QUALITIES.map((q) => (
                  <button
                    key={q.key}
                    type="button"
                    data-testid={`song-${q.testid}`}
                    onClick={() => setQuality(q.key)}
                    title={q.hint}
                    className={`flex-1 rounded-xl px-2 py-2.5 text-sm border transition-all ${
                      quality === q.key
                        ? "bg-fuchsia-500 text-black border-fuchsia-500"
                        : "bg-slate-950/80 border-slate-800 text-slate-300"
                    }`}
                  >
                    {q.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-500">
            Both <span className="text-slate-300">landscape</span> and <span className="text-slate-300">shorts</span> are
            generated automatically — no need to choose. 720p renders roughly twice as fast as 1080p.
          </p>

          <YoutubePublishOptions kind="song" slot="song" value={publishOpts} onChange={setPublishOpts} testidPrefix="song-publish" />

          <button
            data-testid="generate-song-button"
            type="submit"
            disabled={submitting}
            className="w-full bg-fuchsia-500 text-black hover:bg-fuchsia-400 font-semibold px-6 py-4 rounded-xl shadow-lg shadow-fuchsia-500/20 transition-all active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-60 text-base"
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
            {submitting ? "Starting composition..." : "Generate 4 Song Videos"}
          </button>
          <p className="text-xs text-slate-500 text-center">Music composition takes ~1-2 min; the 4 videos then render one at a time.</p>
        </form>
      </div>
    </div>
  );
};
